import React, { useState, useEffect, useRef } from "react";
import { Chessboard } from "react-chessboard";
import { Chess } from 'chess.js';
import { useTranslation } from 'react-i18next';
import { HEADER_HEIGHT } from '../config/Constants';
import PuzzleService from "../services/PuzzleService";
import GameUtils from '../utils/gameUtils';

import { View, Text, TouchableOpacity } from 'react-native';

// Puzzle of the Day. Three puzzles per day (easy / medium / hard); the player is
// the side to move and must find the forced mate. Each attacker move is confirmed
// server-side (the solution never reaches the browser); the forced defender reply
// is played automatically. Sumo push/check rules come from GameUtils.
export default function Puzzle() {
  const { t } = useTranslation();

  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth, height: window.innerHeight,
  });
  const [puzzles, setPuzzles] = useState([]);       // up to 3, ordered by slot
  const [selected, setSelected] = useState(0);      // index into puzzles
  const [position, setPosition] = useState(null);   // FEN driving the board
  const [status, setStatus] = useState('loading');  // loading|none|playing|solved|error
  const [message, setMessage] = useState("");
  const [lastArrow, setLastArrow] = useState([]);
  const [solved, setSolved] = useState({});         // {puzzleId: true}

  const gameRef = useRef(null);
  const lastMoveRef = useRef(null);
  const plyRef = useRef(0);
  const guRef = useRef(null);
  const busyRef = useRef(false);
  const puzzleRef = useRef(null);   // the currently-selected puzzle

  const DIFF_LABELS = {
    easy: t('difficulty_easy') || 'Easy',
    medium: t('difficulty_medium') || 'Medium',
    hard: t('difficulty_hard') || 'Hard',
  };

  useEffect(() => {
    const onResize = () => setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load the daily set once.
  useEffect(() => {
    let cancelled = false;
    PuzzleService.getDaily()
      .then((resp) => {
        if (cancelled) return;
        const list = (resp.data && resp.data.puzzles) || [];
        if (!list.length) { setStatus('none'); return; }
        // restore solved state for today from localStorage
        let done = {};
        try {
          const key = 'sumo_puzzle_solved_' + (list[0].puzzle_date || 'today');
          done = JSON.parse(localStorage.getItem(key) || '{}');
        } catch (e) { done = {}; }
        setSolved(done);
        setPuzzles(list);
        // start on the first unsolved puzzle, else the first
        const firstUnsolved = list.findIndex((p) => !done[p.id]);
        selectPuzzle(list, firstUnsolved >= 0 ? firstUnsolved : 0, done);
      })
      .catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up the board for puzzle at `idx`.
  const selectPuzzle = (list, idx, doneMap) => {
    const pz = list[idx];
    if (!pz) return;
    setSelected(idx);
    puzzleRef.current = pz;
    gameRef.current = new Chess(pz.fen);
    lastMoveRef.current = pz.last_move || null;
    plyRef.current = 0;
    guRef.current = new GameUtils(null, pz.side_to_move === 'w', false, true, true);
    busyRef.current = false;
    setPosition(pz.fen);
    setLastArrow(pz.last_move
      ? [[pz.last_move.split(',')[0], pz.last_move.split(',')[1], 'rgba(0,0,0,0.4)']] : []);
    const done = (doneMap || solved)[pz.id];
    setStatus(done ? 'solved' : 'playing');
    setMessage(done ? (t('puzzle_solved') || 'Solved!') : '');
  };

  const markSolved = (pz) => {
    setSolved((prev) => {
      const next = { ...prev, [pz.id]: true };
      try {
        localStorage.setItem('sumo_puzzle_solved_' + (pz.puzzle_date || 'today'), JSON.stringify(next));
      } catch (e) { /* ignore */ }
      return next;
    });
  };

  const applyMove = (move, moverColor) => {
    const working = new Chess(gameRef.current.fen());
    guRef.current.isWhite = moverColor === 'w';
    guRef.current.myTurn = true;
    const res = guRef.current.makeAMove(move, moverColor, false, working, lastMoveRef.current, () => {});
    if (!res || res.error) return null;
    gameRef.current = new Chess(res.currentFen);
    lastMoveRef.current = res.lastMoveAsString;
    return res;
  };

  const buildMove = (source, target, piece) => {
    const move = { from: source, to: target };
    if (piece && piece[1] === 'P' && (target[1] === '8' || target[1] === '1')) move.promotion = 'q';
    return move;
  };

  const onDrop = (source, target, piece) => {
    const pz = puzzleRef.current;
    if (status !== 'playing' || busyRef.current || !pz) return false;

    const humanColor = pz.side_to_move;
    const oppColor = humanColor === 'w' ? 'b' : 'w';
    const prevFen = gameRef.current.fen();
    const prevLast = lastMoveRef.current;
    const myPly = plyRef.current;

    const res = applyMove(buildMove(source, target, piece), humanColor);
    if (!res) { setMessage(t('puzzle_illegal') || 'That is not a legal move.'); return false; }

    setPosition(res.currentFen);
    setLastArrow([[source, target, 'rgba(30,130,255,0.5)']]);
    setMessage("");
    busyRef.current = true;

    PuzzleService.step(pz.id, myPly, `${source},${target}`)
      .then((resp) => {
        const data = resp.data || {};
        if (!data.correct) {
          gameRef.current = new Chess(prevFen);
          lastMoveRef.current = prevLast;
          setPosition(prevFen);
          setLastArrow(prevLast ? [[prevLast.split(',')[0], prevLast.split(',')[1], 'rgba(0,0,0,0.4)']] : []);
          setMessage(t('puzzle_wrong') || 'Not the solution — try again.');
          busyRef.current = false;
          return;
        }
        if (data.solved) {
          plyRef.current = myPly + 1;
          setStatus('solved');
          markSolved(pz);
          setMessage(t('puzzle_solved') || 'Solved! Come back tomorrow for a new puzzle.');
          busyRef.current = false;
          return;
        }
        const [rf, rt] = data.reply.split(',');
        const oppRes = applyMove({ from: rf, to: rt }, oppColor);
        if (oppRes) { setPosition(oppRes.currentFen); setLastArrow([[rf, rt, 'rgba(200,60,60,0.55)']]); }
        plyRef.current = myPly + 2;
        setMessage(t('puzzle_keep_going') || 'Correct! Keep going.');
        busyRef.current = false;
      })
      .catch(() => {
        gameRef.current = new Chess(prevFen);
        lastMoveRef.current = prevLast;
        setPosition(prevFen);
        setMessage(t('puzzle_error') || 'Something went wrong. Try again.');
        busyRef.current = false;
      });
    return true;
  };

  const smallWidth = windowDimensions.width <= 480;
  const prct = smallWidth ? 0.92 : 0.8;
  const minSize = Math.min(windowDimensions.width, windowDimensions.height - HEADER_HEIGHT - 160) * prct;

  if (status === 'loading') {
    return <View style={{ alignItems: 'center', marginTop: 40 }}>
      <Text style={{ color: '#fff', fontSize: 18 }}>{t('loading') || 'Loading…'}</Text></View>;
  }
  if (status === 'none' || status === 'error') {
    return <View style={{ alignItems: 'center', marginTop: 40 }}>
      <Text style={{ color: '#fff', fontSize: 18, textAlign: 'center', maxWidth: 400 }}>
        {status === 'none' ? (t('puzzle_none') || 'No puzzle today — check back soon.')
          : (t('puzzle_error') || 'Could not load the puzzle.')}</Text></View>;
  }

  const pz = puzzleRef.current;
  const toMove = pz.side_to_move === 'w' ? (t('white_to_move') || 'White to move')
    : (t('black_to_move') || 'Black to move');
  const goal = (t('puzzle_goal') || 'Mate in %d').replace('%d', pz.mate_in);

  return (
    <View style={{ alignItems: 'center', marginTop: 18 }}>
      <Text style={{ color: '#fff', fontSize: 22, fontWeight: 'bold', marginBottom: 10 }}>
        {t('puzzle_of_the_day') || 'Puzzle of the Day'}
      </Text>

      {/* difficulty switcher */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 12 }}>
        {puzzles.map((p, i) => {
          const isSel = i === selected;
          const isDone = !!solved[p.id];
          return (
            <TouchableOpacity key={p.id} onPress={() => selectPuzzle(puzzles, i)}
              style={{
                paddingVertical: 7, paddingHorizontal: 16, borderRadius: 8,
                backgroundColor: isSel ? '#A1291F' : 'rgba(255,255,255,0.10)',
                borderWidth: 1, borderColor: isSel ? '#D1A038' : 'rgba(255,255,255,0.15)',
              }}>
              <Text style={{ color: '#fff', fontWeight: isSel ? 'bold' : 'normal', fontSize: 15 }}>
                {(DIFF_LABELS[p.difficulty_name] || p.difficulty_name)}{isDone ? '  ✓' : ''}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      <Text style={{ color: '#f5c542', fontSize: 15, marginBottom: 12 }}>{toMove} · {goal}</Text>

      <View style={{ width: minSize + 'px' }}>
        <Chessboard
          id="PuzzleBoard"
          position={position}
          onPieceDrop={onDrop}
          arePiecesDraggable={status === 'playing'}
          customArrows={lastArrow}
          boardOrientation={pz.side_to_move === 'w' ? 'white' : 'black'}
          customBoardStyle={{ boxShadow: '0 5px 25px rgba(0,0,0,0.5)', borderRadius: '6px' }}
          customLightSquareStyle={{ backgroundColor: '#D1A038' }}
          customDarkSquareStyle={{ backgroundColor: '#A1291F' }}
        />
      </View>

      {message !== '' && (
        <Text style={{
          color: status === 'solved' ? '#5ad469' : '#fff', fontSize: 17, marginTop: 16,
          textAlign: 'center', maxWidth: Math.max(minSize, 400),
        }}>{message}</Text>
      )}
    </View>
  );
}
